import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { OTelSpan } from "./types";
import { TraceTree } from "./TraceTree";
import { SpanDetail } from "./SpanDetail";
import { buildTraceGroups } from "./utils";
import { Skeleton } from "@/components/shadcn/skeleton";
import { Activity, LayoutPanelLeft, RefreshCw } from "lucide-react";
import { NoxusEmptyBlock } from "@/components/UI/NoxusEmptyBlock";
import { cn } from "@/lib/utils";

export * from "./types";

export interface SpanViewerProps {
  runId?: string;
  fetchUrl?: string;
  groupId?: string;
  className?: string;
  /** Conversation mode: offer a toggle between the latest turn's trace and
   * the full turn history. Workflow surfaces leave this off. */
  enableTurnFilter?: boolean;
}

export const SpanViewer: React.FC<SpanViewerProps> = ({
  runId,
  fetchUrl,
  groupId,
  className = "",
  enableTurnFilter = false,
}) => {
  const [selectedSpan, setSelectedSpan] = useState<OTelSpan | null>(null);
  const [latestTurnOnly, setLatestTurnOnly] = useState(true);

  const {
    data: spansData,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["run-spans", runId, fetchUrl],
    queryFn: async () => {
      const url =
        fetchUrl ?? `/api/backend/groups/${groupId}/runs/${runId}/spans`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to fetch spans: ${res.status}`);
      return res.json();
    },
    enabled: !!(fetchUrl || (runId && groupId)),
    staleTime: 30_000,
  });

  const spans = spansData?.spans;
  const traceGroups = useMemo(() => {
    if (spans) {
      return buildTraceGroups(spans);
    }
    return [];
  }, [spans]);

  const visibleGroups = useMemo(() => {
    if (!enableTurnFilter || !latestTurnOnly || traceGroups.length <= 1) {
      return traceGroups;
    }
    // A turn starts at its agent-run trace; side traces that follow it (e.g.
    // title generation) belong to the same turn.
    const isAgentGroup = (group: (typeof traceGroups)[number]) =>
      group.roots.some(
        (root) =>
          root.name.startsWith("invoke_agent") ||
          root.attributes["openinference.span.kind"] === "AGENT"
      );
    const lastAgentIndex = traceGroups.findLastIndex(isAgentGroup);
    return traceGroups.slice(lastAgentIndex >= 0 ? lastAgentIndex : -1);
  }, [traceGroups, enableTurnFilter, latestTurnOnly]);

  const visibleSpanCount = useMemo(() => {
    const countTree = (span: OTelSpan): number =>
      1 + (span.children ?? []).reduce((acc, c) => acc + countTree(c), 0);
    return visibleGroups.reduce(
      (acc, g) => acc + g.roots.reduce((a, r) => a + countTree(r), 0),
      0
    );
  }, [visibleGroups]);

  // Loading
  if (isLoading) {
    return (
      <div className="flex flex-col h-full bg-white">
        <div className="h-12 border-b border-[#e2e8f0] px-4 flex items-center">
          <Skeleton className="h-4 w-32 bg-[#f8fafc]" />
        </div>
        <div className="p-4 space-y-2">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full bg-[#f8fafc]" />
          ))}
        </div>
      </div>
    );
  }

  // Error
  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-white text-[#64748b]">
        <Activity className="size-8 mb-3 opacity-20" />
        <span className="text-[13px]">Failed to load traces.</span>
        <button
          onClick={() => refetch()}
          className="mt-4 px-3 py-1.5 text-[12px] font-medium bg-[#f8fafc] hover:bg-[#e2e8f0] rounded-md transition-colors text-[#0f172a]"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Empty
  if (!spansData || spansData.span_count === 0) {
    return (
      <div data-testid="trace-empty" className="h-full">
        <NoxusEmptyBlock
          size="sm"
          icon={LayoutPanelLeft}
          title="No traces"
          description="No traces recorded for this run."
        />
      </div>
    );
  }

  return (
    <div
      data-testid="trace-explorer"
      className={`flex w-full h-full bg-white text-[#0f172a] font-sans antialiased overflow-hidden shadow-sm ${className}`}
    >
      {/* Left Pane: Trace Tree */}
      <div className="w-1/3 min-w-[280px] max-w-[450px] border-r border-[#e2e8f0] flex flex-col">
        <div className="px-4 py-3 border-b border-[#e2e8f0] bg-white flex items-center justify-between">
          <h2 className="text-sm font-medium text-[#0f172a] tracking-tight flex items-center gap-2">
            <Activity className="size-4 text-[#64748b]" />
            Trace Explorer
          </h2>
          <div className="flex items-center gap-3">
            <span
              data-testid="trace-span-count"
              className="text-xs text-[#64748b] font-mono"
            >
              {visibleSpanCount} spans
            </span>
            <button
              className="flex items-center justify-center size-6 rounded-md text-[#94a3b8] hover:text-[#0f172a] hover:bg-[#f8fafc] transition-colors"
              onClick={() => refetch()}
              disabled={isFetching}
              title="Refresh traces"
              aria-label="Refresh traces"
            >
              <RefreshCw
                className={cn("size-3", isFetching && "animate-spin")}
              />
            </button>
          </div>
        </div>
        {enableTurnFilter && traceGroups.length > 1 && (
          <div className="px-4 py-2 border-b border-[#e2e8f0] bg-white flex items-center gap-1">
            <button
              className={cn(
                "px-2 py-1 text-[11px] font-medium rounded-md transition-colors",
                latestTurnOnly
                  ? "bg-[#e2e8f0] text-[#0f172a]"
                  : "text-[#64748b] hover:bg-[#f8fafc] hover:text-[#0f172a]"
              )}
              onClick={() => setLatestTurnOnly(true)}
            >
              Latest turn
            </button>
            <button
              className={cn(
                "px-2 py-1 text-[11px] font-medium rounded-md transition-colors",
                !latestTurnOnly
                  ? "bg-[#e2e8f0] text-[#0f172a]"
                  : "text-[#64748b] hover:bg-[#f8fafc] hover:text-[#0f172a]"
              )}
              onClick={() => setLatestTurnOnly(false)}
            >
              All turns ({traceGroups.length})
            </button>
          </div>
        )}
        <div className="flex-1 overflow-hidden">
          <TraceTree
            groups={visibleGroups}
            selectedSpanId={selectedSpan?.id || null}
            onSelectSpan={setSelectedSpan}
          />
        </div>
      </div>

      {/* Right Pane: Span Detail */}
      <div className="flex-1 flex flex-col overflow-hidden bg-white">
        <SpanDetail span={selectedSpan} />
      </div>
    </div>
  );
};
